import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Home, Eye, Heart, Calendar, FileText, Handshake, MessageSquare, Loader2, ShieldCheck, Download, Send, Phone, Mail, Clock, CheckCircle2, Circle, TrendingUp } from "lucide-react";
import { API } from "./api";

const fmt = (n) => { try { return `${parseInt(n).toLocaleString("fr-FR")} €`; } catch { return n; } };
const dt = (s) => (s ? new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "");
const OFFER_LABEL = { received: "Reçue", countered: "Contre-proposée", accepted: "Acceptée", refused: "Refusée", expired: "Expirée", pending: "En cours" };

export default function SellerPortal() {
  const { token } = useParams();
  const [d, setD] = useState(undefined);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const load = () => fetch(`${API}/seller/portal/${token}`).then((r) => (r.ok ? r.json() : null)).then(setD).catch(() => setD(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (d === undefined) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-10 h-10 border-2 border-[#C19A6B] border-t-transparent rounded-full animate-spin" /></div>;
  if (d === null) return <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center"><p className="text-zinc-400">Lien invalide ou expiré.</p></div>;

  const l = d.listing, adv = d.advisor;
  const send = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/seller/portal/${token}/message`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: msg }) });
      if (res.ok) { setMsg(""); load(); }
    } catch (_) {}
    setSending(false);
  };
  const Card = ({ icon: Ic, title, children, testid }) => (
    <div className="border border-white/10 bg-[#0d0d10] p-5" data-testid={testid}>
      <h3 className="text-white text-sm font-medium flex items-center gap-2 mb-3"><Ic className="w-4 h-4 text-[#C19A6B]" /> {title}</h3>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-16 pb-20 px-5" data-testid="seller-portal">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="flex items-center gap-2 text-[#C19A6B]"><Home className="w-5 h-5" /><span className="text-xs uppercase tracking-[0.25em]">Espace vendeur OFF MARKETTE</span></div>

        {/* Suivi annonce */}
        <div className="border border-[#C19A6B]/30 bg-gradient-to-br from-[#C19A6B]/10 to-transparent overflow-hidden">
          {l.cover && <img src={l.cover} alt="" className="w-full h-48 object-cover" />}
          <div className="p-5">
            <h1 className="text-2xl font-light">{l.title}</h1>
            <p className="text-sm text-zinc-400">{[l.type, l.surface ? `${l.surface} m²` : null, l.rooms ? `${l.rooms} pièces` : null, l.city].filter(Boolean).join(" • ")}</p>
            {l.price ? <p className="text-2xl text-[#C19A6B] font-light mt-2">{fmt(l.price)}</p> : null}
            <div className="flex flex-wrap gap-4 mt-4 text-sm text-zinc-300">
              <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2 py-1 border ${l.published ? "border-[#10B981]/40 text-[#10B981]" : "border-white/20 text-zinc-400"}`}>{l.published ? "En ligne" : "Brouillon"}</span>
              <span className="inline-flex items-center gap-1.5"><Eye className="w-4 h-4 text-zinc-500" /> {l.views} vues</span>
              <span className="inline-flex items-center gap-1.5"><Heart className="w-4 h-4 text-zinc-500" /> {l.favorites} favoris</span>
            </div>
          </div>
        </div>

        {/* Frise de suivi croisé : visites → offre → compromis */}
        {(() => {
          const offers = d.offers || [];
          const hasOffer = offers.length > 0;
          const accepted = offers.some((o) => o.status === "accepted");
          const st = String(l.status || "").toLowerCase();
          const compromis = accepted || ["sous_compromis", "sous compromis", "vendu", "sold", "compromis"].some((x) => st.includes(x));
          const steps = [
            { label: "En ligne", done: l.published !== false, sub: `${l.views || 0} vues` },
            { label: "Visites", done: (d.visits || []).length > 0, sub: `${(d.visits || []).length} visite(s)` },
            { label: "Offre reçue", done: hasOffer, sub: hasOffer ? `${offers.length} offre(s)` : "" },
            { label: "Offre acceptée", done: accepted, sub: "" },
            { label: "Compromis / Signature", done: compromis, sub: "" },
          ];
          const doneCount = steps.filter((s) => s.done).length;
          const pct = Math.round(((doneCount - 1) / (steps.length - 1)) * 100);
          return (
            <Card icon={TrendingUp} title="Suivi de votre vente" testid="seller-timeline">
              <div className="relative pt-2 pb-1">
                <div className="absolute left-3 right-3 top-[18px] h-[2px] bg-white/10" />
                <div className="absolute left-3 top-[18px] h-[2px] bg-[#C19A6B] transition-all duration-500" style={{ width: `calc((100% - 24px) * ${Math.max(0, pct) / 100})` }} />
                <div className="relative flex justify-between">
                  {steps.map((s, i) => (
                    <div key={i} className="flex flex-col items-center text-center flex-1" data-testid={`seller-step-${i}`}>
                      {s.done ? <CheckCircle2 className="w-6 h-6 text-[#C19A6B] bg-[#0d0d10] rounded-full" /> : <Circle className="w-6 h-6 text-zinc-600 bg-[#0d0d10] rounded-full" />}
                      <span className={`mt-2 text-[10px] sm:text-xs leading-tight px-1 ${s.done ? "text-white" : "text-zinc-500"}`}>{s.label}</span>
                      {s.sub && <span className="text-[9px] text-[#C19A6B]/80 mt-0.5">{s.sub}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          );
        })()}
        <Card icon={ShieldCheck} title="Mon conseiller" testid="seller-advisor">
          <div className="flex items-center gap-4">
            {adv.photo ? <img src={adv.photo} alt="" className="w-14 h-14 rounded-full object-cover" /> : <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center text-[#C19A6B] text-lg">{(adv.name || "?")[0]}</div>}
            <div className="text-sm">
              <p className="text-white font-medium">{adv.name}</p>
              {adv.company && <p className="text-zinc-500 text-xs">{adv.company}</p>}
              <div className="flex gap-3 mt-1 text-xs text-zinc-400">
                {adv.phone && <a href={`tel:${adv.phone}`} className="inline-flex items-center gap-1 hover:text-[#C19A6B]"><Phone className="w-3 h-3" /> {adv.phone}</a>}
                {adv.email && <a href={`mailto:${adv.email}`} className="inline-flex items-center gap-1 hover:text-[#C19A6B]"><Mail className="w-3 h-3" /> Email</a>}
              </div>
            </div>
          </div>
        </Card>

        {/* Visites + comptes-rendus */}
        <Card icon={Calendar} title={`Visites (${d.visits.length})`} testid="seller-visits">
          {d.visits.length === 0 ? <p className="text-sm text-zinc-500">Aucune visite programmée pour l'instant.</p> : (
            <div className="space-y-2">
              {d.visits.map((v) => (
                <div key={v.id} className="flex items-center justify-between border border-white/10 p-2.5 text-sm gap-2">
                  <span className="text-zinc-200 min-w-0">{v.title} <span className="text-zinc-500">· {dt(v.date)} {v.time}</span>{v.acquereur_name && <span className="block text-xs text-[#C19A6B]/90 mt-0.5">👤 {v.acquereur_name}</span>}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    <a href={`${API}/seller/portal/${token}/visit/${v.id}/debrief`} target="_blank" rel="noopener noreferrer" title="Compte-rendu PDF" data-testid={`seller-visit-debrief-${v.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider border border-[#C19A6B]/40 text-[#C19A6B] hover:bg-[#C19A6B]/10 rounded px-2 py-1 transition-colors"><FileText className="w-3 h-3" /> CR</a>
                    <span className="text-[10px] uppercase text-zinc-400 border border-white/15 rounded px-1.5 py-0.5">{v.status === "terminee" ? "Terminée" : v.status === "confirmee" ? "Confirmée" : "Prévue"}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
          {l.has_report && (
            <a href={`${API}/seller/portal/${token}/report`} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 px-3 py-2 transition-colors" data-testid="seller-report-pdf"><FileText className="w-4 h-4" /> Compte-rendu de visite (PDF)</a>
          )}
        </Card>

        {/* Offres */}
        <Card icon={Handshake} title={`Offres d'achat (${d.offers.length})`} testid="seller-offers">
          {d.offers.length === 0 ? <p className="text-sm text-zinc-500">Aucune offre reçue pour l'instant.</p> : (
            <div className="space-y-2">
              {d.offers.map((o, i) => (
                <div key={i} className="border border-white/10 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-lg text-white font-light">{fmt(o.amount)}</span>
                    <span className="text-[10px] uppercase tracking-wider text-[#C19A6B] border border-[#C19A6B]/30 px-2 py-1">{OFFER_LABEL[o.status] || o.status}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">{o.buyer} · {o.financing === "cash" ? "Comptant" : "Financement"} · {dt(o.created_at)}{o.documents ? ` · ${o.documents} doc.` : ""}</p>
                  {o.seller_token && <a href={`/vendeur/${o.seller_token}`} className="text-xs text-[#C19A6B] hover:underline">Répondre à cette offre →</a>}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Documents */}
        <Card icon={FileText} title="Documents & médias" testid="seller-documents">
          <p className="text-sm text-zinc-400 mb-2">{(l.images || []).length} photo(s) du bien.</p>
          <div className="flex flex-wrap gap-2">
            {(l.images || []).slice(0, 8).map((im, i) => <img key={i} src={im} alt="" className="w-16 h-12 object-cover border border-white/10" />)}
          </div>
        </Card>

        {/* Échanges conseiller */}
        <Card icon={MessageSquare} title="Échanges avec mon conseiller" testid="seller-messages">
          <div className="space-y-2 mb-3 max-h-64 overflow-y-auto">
            {d.messages.length === 0 ? <p className="text-sm text-zinc-500">Aucun message. Écrivez à votre conseiller ci-dessous.</p> :
              d.messages.map((m, i) => (
                <div key={i} className={`text-sm p-2.5 max-w-[85%] ${m.from === "seller" ? "ml-auto bg-[#C19A6B]/15 border border-[#C19A6B]/20" : "bg-white/5 border border-white/10"}`}>
                  <p className="text-zinc-200">{m.text}</p>
                  <p className="text-[10px] text-zinc-500 mt-1 flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {dt(m.at)} · {m.from === "seller" ? "Vous" : "Conseiller"}</p>
                </div>
              ))}
          </div>
          <div className="flex gap-2">
            <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Votre message…" className="flex-1 bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C19A6B]" data-testid="seller-msg-input" />
            <button onClick={send} disabled={sending || !msg.trim()} className="bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white px-4 transition-colors" data-testid="seller-msg-send">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
          </div>
        </Card>

        <p className="text-[11px] text-zinc-600 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#C19A6B]" /> Accès sécurisé sans compte, réservé au vendeur de ce bien.</p>
      </div>
    </div>
  );
}
